import React from "react";
import { connect } from "react-redux";

import jsPDF from "jspdf";
import { saveAs } from "file-saver";

import olExtElement from 'ol-ext/util/element';

// Monkey-patch addListener so touch starts are passive
const origAddListener = olExtElement.addListener;
olExtElement.addListener = function(el, type, fn) {
  const options = (type === 'touchstart' || type === 'touchmove' || type === 'wheel')
    ? { passive: true }
    : false;
  el.addEventListener(type, fn, options);
  // If ol-ext did any bookkeeping, call origAddListener as well:
  return origAddListener.call(this, el, type, fn);
};


// imports OL specifics
import "ol/ol.css";
import "ol-ext/control/LayerSwitcherImage.css";
import PrintDialog from "ol-ext/control/PrintDialog";
import Legend from "ol-ext/legend/Legend";
import LegendControl from "ol-ext/control/Legend";
import { Map, View } from "ol";
import LayerSwitcherImages from "ol-ext/control/LayerSwitcherImage";
import "ol-ext/layer/GetPreview";
import { fromLonLat, transform } from "ol/proj";
import { Group as LayerGroup } from "ol/layer";
import { Style, Image as olImage, Icon } from "ol/style";
import WMTS from "ol/source/WMTS";
import { XYZ } from "ol/source";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import { OSM } from "ol/source";
import { DragRotateAndZoom, defaults as defaultInteractions } from "ol/interaction";
import { ScaleLine, Zoom, Rotate, defaults as defaultControls } from "ol/control";
import { motion } from "framer-motion";
import Drawing from "geoportal-extensions-openlayers/src/OpenLayers/Controls/Drawing";
import MeasureLength from "geoportal-extensions-openlayers/src/OpenLayers/Controls/Measures/MeasureLength";
import MeasureArea from "geoportal-extensions-openlayers/src/OpenLayers/Controls/Measures/MeasureArea";

import { IGN_KEY, INIT_CENTER, IGN_RESOLUTION } from "../../constants";

import MapGeoJsonService from "../../services/MapGeoJsonService";
import MapLayerServices from "../../services/MapLayerServices";
import PrintService from "../../services/PrintService";
import FormsService from "../../services/FormsService";
import "ol-ext/dist/ol-ext.css";
import styles from "./BaseMap.scss";
import { Services } from "geoportal-extensions-openlayers";
import WebGLTile from "ol/layer/WebGLTile"; // Import WebGLTile
import TileLayer from 'ol/layer/Tile';
import { hasWorkingWebGL } from "../utilities/webgl";

class BaseMap extends React.Component {
   constructor(props) {
      super(props);
      const userLocalisation = JSON.parse(props.localisation);
      this.userCentroid =
         userLocalisation && userLocalisation.info && userLocalisation.info !== ""
            ? [Number(userLocalisation.info.x), Number(userLocalisation.info.y)]
            : INIT_CENTER;
      this.mapGeoJsonService = new MapGeoJsonService();
      this.useWebGL = hasWorkingWebGL();
      this.mapLayerService = new MapLayerServices( this.useWebGL);
      this.printService = new PrintService();
      this.formsService = new FormsService();
      this.props.setIgnKey(props.ignkey);
      this.props.setGeoServerUrp(props.geoserverurl);

      this.toggleMapToolsVariants = {
         open: {
            position: "absolute",
            top: "385px",
            right: "11px",
         },
         close: {
            position: "absolute",
            top: "385px",
            right: "-50px",
         },
      };

      PrintDialog.prototype.formats = [
         {
            title: "Copier dans le presse-papier",
            imageType: "image/png",
            clipboard: true,
         },
         {
            title: "Enregistrer en jpeg",
            imageType: "image/jpeg",
            quality: 0.92,
         },
         {
            title: "Enregistrer en png",
            imageType: "image/png",
            quality: 0.92,
         },
         {
            title: "Enregistrer en PDF",
            imageType: "image/jpeg",
            quality: 0.92,
            pdf: true,
         },
      ];
      PrintDialog.prototype.scales = {
         " 500": "1/500",
         " 1000": "1/1.000",
         " 1500": "1/1.500",
         " 2000": "1/2.000",
         " 2500": "1/2.500",
         " 5000": "1/5.000",
         " 10000": "1/10.000",
         " 25000": "1/25.000",
         " 50000": "1/50.000",
      };
      PrintDialog.prototype.paperSize = {
         A4: [210, 297],
         A3: [297, 420],
         A2: [420, 594],
         A1: [594, 841],
      };
      this.printControl = new PrintDialog({
         lang: "fr",
      });
      this.printControl.on('beforeprint', e => {
         const [w, h] = e.print.size;  // mm dimensions
         const pageEl = document.querySelector('.ol-ext-print-dialog .ol-print-map .ol-page');
         if (!pageEl) return;
         console.log('printControl', w,h)
         pageEl.style.width = `${w}mm`;
         pageEl.style.height = `${h}mm`;
         pageEl.style.transform = 'none';
         pageEl.style.position = 'static';
         const canvas = pageEl.querySelector('canvas');
         if (canvas) canvas.style.transform = 'none';
      });

      this.printControl.setSize("A4");
      //this.printControl.setMargin(5);

      this.printControl.on(["print", "error"], function (e) {
      // Print success

         if (e.image) {
            if (e.pdf) {
               // Export pdf using the print info
               let pdf = new jsPDF({
                  orientation: e.print.orientation,
                  unit: e.print.unit,
                  //format: e.print.size
               });
               pdf.addImage(
                  e.image,
                  "JPEG",
                  e.print.position[0],
                  e.print.position[0],
                  e.print.imageWidth,
                  e.print.imageHeight
               );
               pdf.save(e.print.legend ? "legend.pdf" : "map.pdf");
               
            } else {
               // Save image as file
               e.canvas.toBlob(
                  function (blob) {
                     let name =
                        (e.print.legend ? "legend." : "map.") +
                        e.imageType.replace("image/", "");
                     saveAs(blob, name);
                  },
                  e.imageType,
                  e.quality
               );
            }
         } else {
            console.warn("No canvas to export");
         }
      });
   }

   buildLegend() {
      let legend = new Legend({
         title: "Légende ",
         margin: 3,
         size: [60, 30],
      });
      this.props.layers_stack.map((layer, index) => {
         let src =
            this.props.geoserverApiUrl +
            "?&REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=25&HEIGHT=25&LAYER=" +
            layer +
            "&legend_options=fontSize:8;forceLabels:on;bgColor=0xFFFFFF;mx=0.5";
         if (src) {
            let item = {
               title: "",
               typeGeom: "Point",
               style: new Style({
                  image: new Icon({
                     src: src,
                     crossOrigin: "anonymous", // Enable print
                  }),
               }),
            };
            legend.addItem(item);
         }
      });

      let legendCtrl = new LegendControl({
         legend: legend,
         //target: legWrap
      });
      this.map.addControl(legendCtrl);
   }

   async componentDidMount() {
      //// to store initial Windows size ////////////////
      this.props.updateWindowSize(window.innerWidth);
      //// then listener ///////
      window.addEventListener(
         "resize",
         window._.debounce(() => {
            this.props.updateWindowSize(window.innerWidth);
         }, 200)
      );
      ///// end windows size /////////////////////

      // Initialize default Layers

      const osmLayer =  this.useWebGL
         ? new WebGLTile({
            source: new OSM({ crossOrigin: 'anonymous' }),
            title: 'OSM',
            name: 'OSM',
            visible: false,
            baseLayer: true,
         })
         : new TileLayer({
            source: new OSM({ crossOrigin: 'anonymous' }),
            title: 'OSM',
            name: 'OSM',
            visible: false,
            baseLayer: true,
         });
      osmLayer.set('name', 'osm');
      osmLayer.setZIndex(0); // Lowest z-index

      const osmGray =  this.useWebGL
         ? new WebGLTile({
            title: 'Neutre',
            baseLayer: true,
            visible: false,
            source: new XYZ({
               url: 'http://{1-4}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
               crossOrigin: 'anonymous',
            }),
         })
         :new TileLayer({
            title: 'Neutre',
            baseLayer: true,
            visible: false,
            source: new XYZ({
               url: 'http://{1-4}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
               crossOrigin: 'anonymous',
            }),
         });
      osmGray.set('name', 'osmGray');
      osmGray.setZIndex(0);

      const ignSource = new WMTS({
         url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0',
         layer: 'ORTHOIMAGERY.ORTHOPHOTOS',
         matrixSet: 'PM',
         format: 'image/jpeg',
         style: 'normal',
         crossOrigin: 'Anonymous',
         tileGrid: new WMTSTileGrid({
            origin: [-20037508, 20037508],
            resolutions: IGN_RESOLUTION,
            matrixIds: Array.from({ length: 20 }, (_, i) => i.toString()),
         }),
      });

      const ignLayer =  this.useWebGL
         ? new WebGLTile({
            title: 'ORTHO',
            name: 'ORTHO',
            baseLayer: true,
            visible: true,
            source: ignSource,
         })
         : new TileLayer({
            title: 'ORTHO',
            name: 'ORTHO',
            baseLayer: true,
            visible: true,
            source: ignSource,
         });

      ignLayer.set('name', 'IGN');
      ignLayer.setZIndex(0);

      // Similarly update mixte and dummyLayer
      const mixteLayer = new LayerGroup({
         visible: false,
         baseLayer: true,
         title: 'MIXTE',
         layers: [
            this.useWebGL
               ? new WebGLTile({
                  title: 'ORTHO',
                  name: 'ORTHO',
                  visible: true,
                  source: ignSource, // Assuming same source; adjust if different
               })
               : new TileLayer({
                  title: 'ORTHO',
                  name: 'ORTHO',
                  visible: true,
                  source: ignSource,
               }),
             new TileLayer({
                  title: 'ROUTES',
                  visible: true,
                  source: new WMTS({
                     url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0',
                     layer: 'TRANSPORTNETWORKS.ROADS',
                     matrixSet: 'PM',
                     format: 'image/png',
                     style: 'normal',
                     crossOrigin: 'Anonymous',
                     tileGrid: new WMTSTileGrid({
                        origin: [-20037508, 20037508],
                        resolutions: IGN_RESOLUTION,
                        matrixIds: Array.from({ length: 20 }, (_, i) => i.toString()),
                     }),
                  }),
               }),
         ],
      });
      mixteLayer.set('name', 'MIXTE');
      mixteLayer.setZIndex(0);

      const dummyLayer = this.useWebGL
         ? new WebGLTile({
            title: 'DUMMY',
            baseLayer: true,
            visible: false,
            source: new WMTS({
               url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0',
               layer: 'DUMMY_LAYER', // Replace with actual layer
               matrixSet: 'PM',
               format: 'image/png',
               style: 'normal',
               crossOrigin: 'Anonymous',
               tileGrid: new WMTSTileGrid({
                  origin: [-20037508, 20037508],
                  resolutions: IGN_RESOLUTION,
                  matrixIds: Array.from({ length: 20 }, (_, i) => i.toString()),
               }),
            }),
         })
         : new TileLayer({
            title: 'DUMMY',
            baseLayer: true,
            visible: false,
            source: new WMTS({
               url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0',
               layer: 'DUMMY_LAYER', // Replace with actual layer
               matrixSet: 'PM',
               format: 'image/png',
               style: 'normal',
               crossOrigin: 'Anonymous',
               tileGrid: new WMTSTileGrid({
                  origin: [-20037508, 20037508],
                  resolutions: IGN_RESOLUTION,
                  matrixIds: Array.from({ length: 20 }, (_, i) => i.toString()),
               }),
            }),
         });
      dummyLayer.set('name', 'blanc');

      this.mapView = new View({
         center: this.userCentroid,
         zoom: 15,
         minZoom: 9,
         maxZoom: 19,
         rotation: 0.01,
      });

      this.map = new Map({
         interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
         target: 'map',
         layers: [mixteLayer, osmLayer, osmGray, ignLayer],
         // Disabling attribution avoids the error "Uncaught TypeError: t.getAttributions is not a function"
         controls: defaultControls({ attribution: false }).extend([]),
         view: this.mapView,
      });

      // Store map element for retrieval
      let mapElement = "#map";
      $(mapElement).data("map", this.map);

      const drawing = new Drawing({
         draggable: false,
         target: document.getElementById("ign_tools"),
      });

      this.map.addControl(drawing);

      const measureLength = new MeasureLength({
         target: document.getElementById("ign_tools"),
      });
      this.map.addControl(measureLength);

      const measureArea = new MeasureArea({
         target: document.getElementById("ign_tools"),
      });
      this.map.addControl(measureArea);

      this.layerSwitcherImages = new LayerSwitcherImages({
         //target: "ol-controls-wrapper",
         //collapsible: true,
         //collapsed: true,
      });


      this.map.addControl(this.layerSwitcherImages);

      this.map.addControl(new ScaleLine({}));
      this.map.addControl(new Zoom({ target: "ol-controls-wrapper" }));
      this.map.addControl(new Rotate({ target: "ol-controls-wrapper" }));
      this.map.addControl(this.printControl);

      this.map.on("moveend", () => {
         this.props.mapMoveEnd();
      });

      // Initialize layers visibility and fetch thema
      axios
    .get("/Api/thema")
    .then((result) => {
      this.props.setLayersRef(result.data);
      this.props.addLayerToStack("cadastre:parcelles");
    })
    .catch((err) => {
      console.error("Failed to load thema:", err);
    });

   }

   componentDidUpdate(prevProps) {

      const id = this.props.vectoreRemoveFeature;
      if (prevProps.vectoreRemoveFeature !== id && id) {
         // build the exact feature ID you used when adding it:
         const fullId = id.startsWith("parcelles.") ? id : `parcelles.${id}`;
         this.removeFeature(fullId);
         this.props.resetVectorRemoveFeature();
      }

      if (
         prevProps.layers_stack.length !== this.props.layers_stack.length &&
         prevProps.layers_stack.length < this.props.layers_stack.length
      ) {
         //console.log("Adding layer:", this.props.layers_stack);
         this.addLayer(this.props.layers_stack);
      }

      if (
         this.props.layer_remove_index !== prevProps.layer_remove_index &&
         this.props.layer_remove_index !== null &&
         this.props.layer_remove_index.length > 0
      ) {
         const layerToRemove = this.props.layers_stack[this.props.layer_remove_index[0]];
         if (layerToRemove) {
            //console.log("Removing layer:", layerToRemove);
            this.removeLayer(layerToRemove);
         } else {
            console.warn("Layer to remove not found in layers_stack.");
         }
      }

      if (prevProps.centerMap !== this.props.centerMap) {
         //console.log("Centering map:", this.props.centerMap);
         this.centerPointMap(this.props.centerMap);
      }

      if (prevProps.vectorLayer !== this.props.vectorLayer) {
         //console.log("Adding GeoJson layer:", this.props.vectorLayer);
         this.addGeoJsonLayer(this.props.vectorLayer.geoJsonData, this.props.vectorLayer.typeTerr);
      }

      if (prevProps.initForm !== this.props.initForm && this.props.initForm) {
         //console.log("Capturing screen location");
         this.captureScreenLocation();
      }

      if (prevProps.printState !== this.props.printState) {
         //console.log("Printing screenshot");
         this.printScreenShot();
      }

      if (prevProps.openPrintControl !== this.props.openPrintControl) {
         //console.log("Printing control");
         this.printControl.print();
      }

      if (
         prevProps?.userPrefs?.map?.center_lon?.value !==
         this.props?.userPrefs?.map?.center_lon?.value
      ) {
         //console.log("Animating map view to new center");
         this.map.getView().animate({
            center: fromLonLat([
               Number(this.props.userPrefs.map.center_lon.value),
               Number(this.props.userPrefs.map.center_lat.value),
            ]),
            zoom: Number(this.props.userPrefs.map.zoom.value + 2),
            duration: 800,
         });
      }
   }

   removeLayer(ref) {
      this.mapLayerService.removeLayer(this.map, ref);
   }

   removeVectorLayer(layerName) {
      this.mapLayerService.removeVectorLayer(this.map, layerName);
   }

   addLayer(ref) {
      this.mapLayerService.addLayer(this.map, ref);
   }

   addGeoJsonLayer(geoJsonData, typeTerr = null) {
      this.mapGeoJsonService.addGeoJsonLayer(this.map, geoJsonData, typeTerr);
   }

   removeFeature(idFeature) {
      this.mapGeoJsonService.removeFeature(this.map, idFeature);
   }

   centerPointMap(point, zoom = 15) {
      this.map.setView(
         new View({
            center: transform(point, "EPSG:4326", "EPSG:3857"),
            zoom: zoom,
         })
      );
   }

   printScreenShot() {
      this.props.setMapRef(this.map);

      let size = this.map.getSize();
      let viewResolution = this.map.getView().getResolution();

      this.printService
         .printScreenShot(this.map)
         .then(
            (result) => {
               this.props.updatePrintScreenCapture(result);

               this.map.getView().setResolution(viewResolution);
               this.map.setSize(size);
            },
            (err) => {
               console.log(err);
            }
         );
   }

   captureScreenLocation(resolution = 10, scale = 1500) {
      this.formsService
         .captureScreenLocation(this.map, resolution, scale)
         .then(
            (result) => {
               this.props.setLocationCapt(result);
            },
            (err) => {
               console.log(err);
            }
         );
   }

   printMapControl() {
      this.buildLegend();
      this.printControl.print();
   }

   render() {
      return (
         <>
            <div className="eumap vh-100" style={{ height: "100%" }}>
               <div id="map" style={{ width: "100%", height: "100%" }} />
               <div id={"ol-controls-wrapper"}></div>
               <motion.div
                  id="ign_tools_anim_wrapper"
                  animate={this.props.displayMapTools ? "open" : "close"}
                  transition={{ type: "easeInOut", duration: 0.4 }}
                  variants={this.toggleMapToolsVariants}
                  className={"ign_tools_anim-wrapper"}
               >
                  <div id="ign_tools_wrapper">
                     <div id="ign_tools" />
                     <div
                        id={"printMapBtn"}
                        className={"ueBaseBtn printMapBtn"}
                        onClick={(e) => {
                           this.printMapControl();
                        }}
                     >
                        <i className="bi bi-printer"></i>
                     </div>
                  </div>
               </motion.div>
            </div>
         </>
      );
   }
}

const mapStateToProps = (state) => {
   return {
      geoserverApiUrl: state.AppReducer.geoserverApiUrl,
      showCenter: state.LeftPanelReducer.showCenter,
      layers_stack: state.LayersReducer.layers_stack,
      layer_remove_index: state.LayersReducer.layer_remove_index,
      layersInHeading: state.LayersReducer.layersInHeading,
      vectorLayer: state.LayersReducer.vectorLayer,
      centerMap: state.MapReducer.centerMap,
      displayMapTools: state.MapReducer.displayMapTools,
      currentParcelle: state.MapReducer.currentParcelle,
      clearVectorLayer: state.LayersReducer.clearVectorLayer,
      vectoreRemoveFeature: state.GeoJsonReducer.vectoreRemoveFeature,
      initForm: state.FormsReducer.initForm,
      printState: state.PrintReducer.printState,
      showCenterSection: state.LeftPanelReducer.showCenterSection,
      openPrintControl: state.PrintReducer.openPrintControl,
      layers_ref: state.LayersReducer.layers_refs,
      userPrefs: state.MeReducer?.prefs,
   };
};

const mapDispatchTProps = (dispatch) => {
   return {
      setIgnKey: (e) => dispatch({ type: "SET_IGN_API_KEY", data: e }),
      setCurrentEntity: (e) => dispatch({ type: "SET_CURRENT_ENTITY", data: { currentEntity: e } }),
      setGeoServerUrp: (e) => dispatch({ type: "SET_GEOSERVER_API_URL", data: e }),
      setMapRef: (e) => dispatch({ type: "SET_MAP_REF", data: e }),
      mapMoveEnd: (e) => dispatch({ type: "MAP_MOVE_END", data: e }),
      addInitialLayer: (e) => dispatch({ type: "TOGGLE_LAYER_SHOW", data: { layerRef: e } }),
      cleanLayersHeadingRef: (e) => dispatch({ type: "STOCK_HEADING_LAYERS_REFS", data: { layersInHeading: e } }),
      addParcelleToList: (e) => dispatch({ type: "ADD_PARCELLE_TO_LIST", data: { parcelleCode: e } }),
      updateVectorLayer: (e, t) => dispatch({ type: "UPDATE_VECTOR_LAYER", data: { geoJsonData: e, typeTerr: t } }),
      togglePanelTab: (e) => dispatch({ type: "TOGGLE_PANEL_TAB", data: { currentTab: e } }),
      cleanVectorLayer: (e) => dispatch({ type: "CLEAR_VECTOR_LAYER", data: e }),
      removeParcelleFromList: (e) => dispatch({ type: "REMOVE_PARCELLE_FROM_LIST", data: { parcelleCode: e } }),
      resetVectorRemoveFeature: () => dispatch({ type: "RESET_VECTOR_REMOVE_FEATURE" }),
      removeFeature: (e) => dispatch({ type: "REMOVE_FEATURE", data: { featureId: e } }),
      setCurrentCom: (e) => dispatch({ type: "SET_CURRENT_COM", data: { currentCom: e } }),
      setLocationCapt: (e) => dispatch({ type: "SET_LOCATION_CAPT", data: { locationCapt: e } }), //// for forms !!
      updatePrintScreenCapture: (e) => dispatch({ type: "UPDATE_SCREEN_CAPTURE", data: { printScreenData: e } }), /// for print !!
      updateWindowSize: (e) => dispatch({ type: "UPDATE_WINDOWS_SIZE", data: e }),
      setDisplayMapTools: (e) => dispatch({ type: "SET_DISPLAY_MAP_TOOLS", data: e }),
      setLayersRef: (e) => dispatch({ type: "SET_LAYERS_REF", data: e }),
      addLayerToStack: (e) => dispatch({ type: "ADD_LAYER_TO_STACK", data: e }),
      showCurrentSection: (e) => dispatch({ type: "SHOW_CURRENT_SECTION", data: e }),
      setLeftPanelOpen: (e) => dispatch({ type: "SET_LEFT_PANEL_OPEN", data: e }),
   };
};

export default connect(mapStateToProps, mapDispatchTProps)(BaseMap);
